// /signup/complete?session_id=… — kicks off the safety-net finalize so
// the org materializes even if the webhook is delayed, then routes the
// user into the app. Sits OUTSIDE ProtectedRoute because at first paint
// the user has a session but no org membership; ProtectedRoute would
// punt them to the "no org" page otherwise.
//
// Robustness model (post 2026-06-07 incident):
// - Long total budget (~2 min) instead of the old 9-second bailout.
// - Two parallel signals on every tick:
//     (a) POST /signup/complete — the finalize call (Stripe session ↔
//         ensureOrgFromSession). Returns org_id on success.
//     (b) GET  /signup/my-memberships — direct query for ANY non-deleted
//         membership for this user. As soon as the webhook OR the
//         finalize endpoint creates the row (whichever wins the race),
//         this flips. Catches the case where (a) is hanging on Stripe.
//   Either signal succeeding advances the user.
// - Progressive UI: "Setting up…" → "Almost there…" → "Taking longer
//   than usual" + escape-hatch button at 45s so the user is never stuck
//   on a silent spinner.
// - Backstop: services/checkoutCompletion fires a "practice ready"
//   email when the org is created, so a user who closed the tab during a
//   slow webhook can still get pulled back in.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import PageLoader from '../shared/PageLoader';

const POLL_INTERVAL_MS = 1500;        // gap between attempts
const TOTAL_BUDGET_MS  = 120000;      // total time we'll wait before giving up
const ELAPSED_ALMOST   = 15000;       // "Almost there..." copy
const ELAPSED_LONG     = 45000;       // "Taking longer than usual" + escape hatch

export default function SignupCompletePage() {
  // The checkout page persisted the org form for refresh-safety — once we
  // land here the flow is done with it.
  try { sessionStorage.removeItem('ml-signup-orgform'); } catch { /* storage blocked */ }

  const { session, loading: authLoading } = useAuth();
  const { refetchMemberships, switchOrg } = useOrg();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  const [status, setStatus] = useState('finalizing'); // finalizing | done | error
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const ranRef = useRef(false);

  // Re-render every second so the UI can transition between
  // "Setting up..." / "Almost there..." / "Taking longer..." states and
  // reveal the escape-hatch button on schedule.
  useEffect(() => {
    if (status !== 'finalizing') return;
    const start = Date.now();
    const t = setInterval(() => setElapsedMs(Date.now() - start), 1000);
    return () => clearInterval(t);
  }, [status]);

  useEffect(() => {
    if (!sessionId || !session || ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;
    const start = Date.now();

    // Single signal — try the finalize call, swallow "not-yet-ready"
    // (409) but propagate anything else.
    const tryFinalize = async () => {
      try {
        const res = await api.finalizeNewOrgSignup(sessionId);
        return { ok: true, orgId: res.org_id };
      } catch (err) {
        const msg = err.message || '';
        if (/not yet complete|409/i.test(msg)) return { ok: false };
        return { ok: false, hardErr: msg };
      }
    };

    // Second signal — just look for ANY membership for this user. The
    // org row plus the membership get inserted together by
    // ensureOrgFromSession, so this is a clean "is it done?" check.
    const tryMembership = async () => {
      try {
        const { memberships } = await api.getMyMemberships();
        const first = memberships?.[0];
        return first ? { ok: true, orgId: first.org_id } : { ok: false };
      } catch {
        return { ok: false };
      }
    };

    const succeed = async (orgId) => {
      if (cancelled) return;
      await refetchMemberships();
      if (orgId) {
        try { switchOrg(orgId); } catch { /* may not be in list yet */ }
      }
      setStatus('done');
      setTimeout(() => navigate('/app/welcome', { replace: true }), 600);
    };

    (async () => {
      while (!cancelled && Date.now() - start < TOTAL_BUDGET_MS) {
        // Fire both probes in parallel; whichever signals first wins.
        const [fin, mem] = await Promise.all([tryFinalize(), tryMembership()]);
        if (cancelled) return;
        if (fin.ok)  return succeed(fin.orgId);
        if (mem.ok)  return succeed(mem.orgId);
        if (fin.hardErr) {
          setStatus('error');
          setError(fin.hardErr);
          return;
        }
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (cancelled) return;
      // Timed out without either signal. Org may still finish in the
      // background — the "practice ready" email will pull the user back
      // in if it does. Show the persistent-wait UI either way.
      setStatus('error');
      setError('Your payment went through but your workspace is taking longer than expected to provision. We\'ll email you the moment it\'s ready — or you can try opening MediLink now.');
    })();

    return () => { cancelled = true; };
  }, [sessionId, session, navigate, refetchMemberships, switchOrg]);

  if (authLoading) return <PageLoader />;
  if (!session)    return <Navigate to="/login" replace />;
  if (!sessionId)  return <Navigate to="/register" replace />;

  // Progressive copy + UI affordances based on how long we've been at it.
  const isLong   = elapsedMs >= ELAPSED_LONG;
  const isAlmost = elapsedMs >= ELAPSED_ALMOST;
  const finalizingTitle = isLong
    ? 'This is taking longer than usual'
    : isAlmost
      ? 'Almost there…'
      : 'Setting up your practice…';
  const finalizingSub = isLong
    ? "Your payment went through. We're waiting on the final confirmation from Stripe — you can keep waiting or jump straight into MediLink now and we'll finish behind the scenes."
    : isAlmost
      ? 'Provisioning your workspace. Hang tight, this usually wraps up in a few more seconds.'
      : 'Confirming your subscription and creating your workspace. This usually takes just a few seconds.';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #efeef8 0%, #ecebf6 50%, #ecebf6 100%)',
        padding: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{
        maxWidth: 480, width: '100%',
        background: 'white',
        border: '1px solid rgba(11,11,20,0.08)',
        borderRadius: 16,
        padding: '36px 32px',
        textAlign: 'center',
        boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 24px 48px -16px rgba(11,11,20,0.10)',
      }}>
        {status === 'finalizing' && (
          <>
            <Loader2 size={36} strokeWidth={2} style={{ color: '#0da7ca', margin: '0 auto 16px', animation: 'sup-spin 1s linear infinite' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0b0b14', margin: '0 0 8px' }}>{finalizingTitle}</h2>
            <p style={{ fontSize: 13, color: 'rgba(11,11,20,0.55)', margin: 0, lineHeight: 1.55 }}>{finalizingSub}</p>
            {isLong && (
              <button
                onClick={async () => { await refetchMemberships(); navigate('/app', { replace: true }); }}
                style={{
                  marginTop: 20,
                  padding: '10px 18px', borderRadius: 10,
                  background: '#0da7ca', color: 'white', border: 'none',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                Continue to MediLink anyway <ArrowRight size={14} />
              </button>
            )}
          </>
        )}
        {status === 'done' && (
          <>
            <CheckCircle2 size={36} strokeWidth={2} style={{ color: '#10B981', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0b0b14', margin: '0 0 8px' }}>You're all set</h2>
            <p style={{ fontSize: 13, color: 'rgba(11,11,20,0.55)', margin: 0 }}>Taking you into MediLink…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle size={36} strokeWidth={2} style={{ color: '#F59E0B', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0b0b14', margin: '0 0 8px' }}>Almost there</h2>
            <p style={{ fontSize: 13, color: 'rgba(11,11,20,0.55)', margin: '0 0 16px', lineHeight: 1.55 }}>
              {error || "We couldn't finish provisioning your workspace automatically. Your payment is safe — try refreshing in a moment, or contact support."}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/app', { replace: true })}
                style={{ padding: '10px 16px', borderRadius: 10, background: '#0da7ca', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                Open MediLink
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: '10px 16px', borderRadius: 10, background: 'transparent', color: '#0b0b14', border: '1px solid rgba(11,11,20,0.15)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                Try again
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes sup-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
